import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';
import moment from 'moment/moment';
import { ScoreDirection } from '@components-clean/central-monitoring/panels/panel-constants';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import styles from '@components-clean/core/drag-drop-lists-core/drag-drop-item/style.module.scss';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import { getHighlightClass, getScoreDirectionEnum } from '@components-clean/core/rag-component/rag-component';
import { TextAreaController } from '@components-clean/core/text-area';
import Tooltip from '@components-clean/core/tooltip';
import { useUnsavedChangesConfirmation, useUnsavedChangesTracking } from '@hooks-clean/index';
import { HC_RAG_NAMES } from '@interfaces/opra-chart.interface';
import { ObservationType } from '@interfaces-clean/observations.interface';
import { NamedItemType } from '@interfaces-clean/organisationLov.interface';
import { SiteReviewLog } from '@interfaces-clean/review-logs.interface';
import { ObservationDetails } from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/observation-details';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { useTranslation } from '@hooks/index';
import ObservationsApi from '@apis/bff-api/central-monitoring/observation';
import OrganisationLovApi from '@apis/bff-api/central-monitoring/organisationLovs';

interface Props {
    reviewLog: SiteReviewLog;
    studyRef: string | null;
    onSubmit: (data: EditReviewLogForm) => void;
    onClose: () => void;
}

export interface EditReviewLogForm {
    dataset: Date | null;
    association: string | null;
    panel: string | null;
    decision: { id: number; name: string; groupedBy: string; disabled: boolean } | null;
    comments: string | null;
    created_date: Date | null;
    created_by: string;
    updated_date: Date | null;
    updated_by: string | null;
    reviewLogId: number;
}

const COMPONENT_NAME = 'edit-site-review-log-dialog';

const EditSiteReviewLogDialog = ({ reviewLog, onClose, onSubmit, studyRef }: Props) => {
    const { t } = useTranslation('cm');

    const [decisionLov, setDecisionLov] = useState<
        { id: number; name: string; groupedBy: string; disabled: boolean }[]
    >([]);

    const { data: observation, isLoading: loadingObservation } = ObservationsApi.useGetDetails(
        studyRef || '',
        reviewLog.observation_id,
        ObservationType.Site,
    );

    const { data: lovData, isLoading: loadingData } = OrganisationLovApi.useGet(
        studyRef ?? '',
        NamedItemType.ReviewLogDecision,
    );

    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<EditReviewLogForm>({
        mode: 'all',
        defaultValues: {
            dataset: reviewLog.dataset ? new Date(reviewLog.dataset) : null,
            association: reviewLog.association ?? '',
            panel: reviewLog.panel ?? '',
            decision: reviewLog.decision ? { name: reviewLog.decision, id: reviewLog.decision_id } : null,
            comments: reviewLog.comments ?? '',
            created_date: reviewLog.created_date ? new Date(reviewLog.created_date) : null,
            created_by: reviewLog.created_by ?? '',
            updated_date: reviewLog.updatedDate ? new Date(reviewLog.updated_date) : null,
            updated_by: reviewLog.updated_by ?? '',
        },
    });

    useEffect(() => {
        if (lovData && !loadingData) {
            const decisionData = lovData
                .filter((y) => y && y.name)
                .sort((a, b) => {
                    // Sort active items first (true values before false values)
                    if (a?.active !== b?.active) {
                        return a?.active ? -1 : 1;
                    }

                    if (a.name && b.name) {
                        return a.name?.localeCompare(b.name);
                    }

                    return 0;
                })
                .map((data) => ({
                    name: data?.name ?? '',
                    id: data?.id ?? 0,
                    groupedBy: data?.active ? 'Active' : 'Inactive',
                    disabled: reviewLog.decision
                        ? reviewLog.decision.toLowerCase() === data.name?.toLowerCase()
                            ? false
                            : !data.active
                        : !data.active,
                    active: data?.active,
                }));

            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setDecisionLov(decisionData);

            if (reviewLog.decision && reviewLog.decision_id) {
                const currentData = decisionData?.find(
                    (decisionDataList) => decisionDataList?.id === Number(reviewLog.decision_id),
                );

                if (currentData)
                    reset((currentDefaults) => {
                        return { ...currentDefaults, decision: currentData };
                    });
            }
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [lovData]);

    const dialogTitle = `${t('common:edit')} ${t('reviewLogDialog.siteTitle')}: ${reviewLog.record_id}`;
    const primaryBtnLabel = t('common:update');
    const createdBy = `${moment(reviewLog?.created_date).format('DD-MMM-YYYY')} by ${reviewLog?.created_by}`;
    const updatedBy = `${moment(reviewLog?.updated_date).format('DD-MMM-YYYY')} by ${reviewLog?.updated_by}`;

    useUnsavedChangesTracking({
        area: 'central-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'central-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    const isNullOrNaN = (value: number | null) => {
        return value == null || isNaN(value);
    };

    const parseValue = (value: number | null) => (isNullOrNaN(value) ? 'N/A' : value);

    const isFirstReviewLog = reviewLog.is_first_log === 'Yes';

    return (
        <DraggableDialog
            dataTestId="edit-site-review-log-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSubmit)()}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnDisabled={isSubmitting}
            createdUpdatedInfo={<CreatedUpdatedInfo created={createdBy} updated={updatedBy} width={500} />}
            createdUpdatedBackgroundColour="gray"
            initialHeight={reviewLog.is_scatter_plot === 'Yes' ? 718 : 642}
            initialWidth={612}
            additionalInfo={
                <ObservationDetails
                    observation={observation}
                    observationLoading={loadingObservation}
                    type={ObservationType.Site}
                />
            }
            additionalInfoWidth={385}
            additionalInfoBtnLabel={t('observation')}
            bodyPadding="none"
            setHeight="100%"
        >
            <div
                className={cn(
                    reviewLog.is_scatter_plot === 'Yes' ? '' : style.editSiteObservationDialog,
                    style.rightBorderStyle,
                    'k-w-full',
                    'k-h-full',
                )}
            >
                {reviewLog.is_scatter_plot === 'Yes' && (
                    <div>
                        <div
                            className={cn(
                                'k-justify-content-self-start k-flex-row k-flex-auto k-d-flex k-align-items-center',
                            )}
                        >
                            <div
                                className={cn(
                                    style.dividerUpdate,
                                    style.dividerBottom,
                                    'k-d-flex k-flex k-pr-md k-pl-md k-pt-1 k-pb-1',
                                )}
                            >
                                <div className="k-flex k-justify-content-center k-flex-col k-d-flex k-align-items-center k-gap-1 !k-basis-1/2">
                                    <div className={cn(style.labelWidth, 'k-overflow-hidden k-text-ellipsis')}>
                                        {reviewLog.x_axis_title}
                                    </div>
                                    <div className="k-d-flex k-justify-content-center k-flex-row">
                                        <div className="k-flex k-justify-content-center k-d-flex k-align-items-center">
                                            <div className="k-display-inline-flex k-flex-row k-align-items-center">
                                                {isFirstReviewLog
                                                    ? parseValue(reviewLog.x_axis)
                                                    : parseValue(reviewLog.previous_x_axis)}
                                                {!isFirstReviewLog &&
                                                    (getScoreDirectionEnum(reviewLog.x_axis_change_direction) !==
                                                    ScoreDirection.Matching ? (
                                                        <FontAwesomeIcon
                                                            icon={['fass', 'triangle']}
                                                            className={cn(
                                                                {
                                                                    [styles.sortAscIcon]:
                                                                        getScoreDirectionEnum(
                                                                            reviewLog.x_axis_change_direction,
                                                                        ) === ScoreDirection.Upper,
                                                                    [`${styles.sortDescIcon} k-rotate-180`]:
                                                                        getScoreDirectionEnum(
                                                                            reviewLog.x_axis_change_direction,
                                                                        ) === ScoreDirection.Lower,
                                                                },
                                                                styles.ragIconSize,
                                                                'k-pl-2 k-pr-2',
                                                            )}
                                                        />
                                                    ) : (
                                                        <FontAwesomeIcon
                                                            icon={['far', 'hyphen']}
                                                            className={cn(styles.sortAscIcon, 'k-pl-2 k-pr-2')}
                                                        />
                                                    ))}
                                            </div>
                                            {!isFirstReviewLog && parseValue(reviewLog.x_axis)}
                                        </div>
                                    </div>
                                </div>
                                <div className="k-flex k-justify-content-center k-flex-row k-d-flex k-align-items-center !k-flex-basis-2/12">
                                    <div className="k-flex k-justify-content-center k-flex-col k-d-flex k-align-items-center k-gap-1">
                                        <div>{t('reviewLogDialog.score')}</div>
                                        <div className="k-d-flex k-justify-content-center k-flex-row">
                                            <div className="k-flex k-justify-content-center k-d-flex k-align-items-center">
                                                {!isFirstReviewLog &&
                                                    (isNullOrNaN(reviewLog.previous_panel_score) ? (
                                                        'N/A'
                                                    ) : (
                                                        <div
                                                            className={cn(
                                                                {
                                                                    [getHighlightClass(
                                                                        HC_RAG_NAMES[
                                                                            +reviewLog.previous_panel_score_rag
                                                                        ],
                                                                    )]: true,
                                                                },
                                                                style.ragPill,
                                                            )}
                                                        >
                                                            {reviewLog.previous_panel_score}
                                                        </div>
                                                    ))}
                                                {!isFirstReviewLog &&
                                                    (getScoreDirectionEnum(reviewLog.panel_score_change_direction) !==
                                                    ScoreDirection.Matching ? (
                                                        <FontAwesomeIcon
                                                            icon={['fass', 'triangle']}
                                                            className={cn(
                                                                {
                                                                    [styles.sortAscIcon]:
                                                                        getScoreDirectionEnum(
                                                                            reviewLog.panel_score_change_direction,
                                                                        ) === ScoreDirection.Upper,
                                                                    [`${styles.sortDescIcon} k-rotate-180`]:
                                                                        getScoreDirectionEnum(
                                                                            reviewLog.panel_score_change_direction,
                                                                        ) === ScoreDirection.Lower,
                                                                },
                                                                styles.ragIconSize,
                                                                'k-pl-2 k-pr-2',
                                                            )}
                                                        />
                                                    ) : (
                                                        <FontAwesomeIcon
                                                            icon={['far', 'hyphen']}
                                                            className={cn(styles.sortAscIcon, 'k-pl-2 k-pr-2')}
                                                        />
                                                    ))}
                                            </div>
                                            {isNullOrNaN(reviewLog.panel_score) ? (
                                                'N/A'
                                            ) : (
                                                <div
                                                    className={cn(
                                                        {
                                                            [getHighlightClass(
                                                                HC_RAG_NAMES[+reviewLog.panel_score_rag],
                                                            )]: true,
                                                        },
                                                        style.ragPill,
                                                    )}
                                                >
                                                    {reviewLog.panel_score}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="k-justify-content-self-start k-flex-row k-flex-auto k-d-flex k-align-items-center">
                            <div
                                className={cn(
                                    style.dividerUpdate,
                                    style.dividerBottom,
                                    'k-d-flex k-flex k-pr-md k-pl-md k-pt-1 k-pb-1',
                                )}
                            >
                                <div className="k-flex k-justify-content-center k-flex-col k-d-flex k-align-items-center k-gap-1 !k-basis-1/2">
                                    <div
                                        className={cn(
                                            style.labelWidth,
                                            'k-justify-content-center k-overflow-hidden k-text-ellipsis',
                                        )}
                                    >
                                        {reviewLog.y_axis_title}
                                    </div>
                                    <div className="k-d-flex k-justify-content-center k-flex-row">
                                        <div className="k-flex k-justify-content-center k-d-flex k-align-items-center">
                                            <div className="k-display-inline-flex k-flex-row k-align-items-center">
                                                {isFirstReviewLog
                                                    ? parseValue(reviewLog.y_axis)
                                                    : parseValue(reviewLog.previous_y_axis)}
                                                {!isFirstReviewLog &&
                                                    (getScoreDirectionEnum(reviewLog.y_axis_change_direction) !==
                                                    ScoreDirection.Matching ? (
                                                        <FontAwesomeIcon
                                                            icon={['fass', 'triangle']}
                                                            className={cn(
                                                                {
                                                                    [styles.sortAscIcon]:
                                                                        getScoreDirectionEnum(
                                                                            reviewLog.y_axis_change_direction,
                                                                        ) === ScoreDirection.Upper,
                                                                    [`${styles.sortDescIcon} k-rotate-180`]:
                                                                        getScoreDirectionEnum(
                                                                            reviewLog.y_axis_change_direction,
                                                                        ) === ScoreDirection.Lower,
                                                                },
                                                                styles.ragIconSize,
                                                                'k-pl-2 k-pr-2',
                                                            )}
                                                        />
                                                    ) : (
                                                        <FontAwesomeIcon
                                                            icon={['far', 'hyphen']}
                                                            className={cn(styles.sortAscIcon, 'k-pl-2 k-pr-2')}
                                                        />
                                                    ))}
                                            </div>
                                            {!isFirstReviewLog && parseValue(reviewLog.y_axis)}
                                        </div>
                                    </div>
                                </div>
                                <div className="k-flex k-justify-content-center k-flex-row k-d-flex k-align-items-center !k-flex-basis-2/12">
                                    <div className="k-flex k-justify-content-center k-flex-col k-d-flex k-align-items-center k-gap-1">
                                        <Tooltip>
                                            <div
                                                className={cn(style.rightLabelWidth, 'k-text-ellipsis')}
                                                title={reviewLog.y_axis_column_value}
                                            >
                                                {reviewLog.y_axis_column_value}
                                            </div>
                                        </Tooltip>

                                        <div className="k-d-flex k-justify-content-center k-flex-row">
                                            <div className="k-flex k-justify-content-center k-d-flex k-align-items-center">
                                                <div className="k-display-inline-flex k-flex-row k-align-items-center">
                                                    {isFirstReviewLog
                                                        ? parseValue(reviewLog.y_volume)
                                                        : parseValue(reviewLog.previous_y_volume)}
                                                    {!isFirstReviewLog &&
                                                        (getScoreDirectionEnum(reviewLog.y_volume_change_direction) !==
                                                        ScoreDirection.Matching ? (
                                                            <FontAwesomeIcon
                                                                icon={['fass', 'triangle']}
                                                                className={cn(
                                                                    {
                                                                        [styles.sortAscIcon]:
                                                                            getScoreDirectionEnum(
                                                                                reviewLog.y_volume_change_direction,
                                                                            ) === ScoreDirection.Upper,
                                                                        [`${styles.sortDescIcon} k-rotate-180`]:
                                                                            getScoreDirectionEnum(
                                                                                reviewLog.y_volume_change_direction,
                                                                            ) === ScoreDirection.Lower,
                                                                    },
                                                                    styles.ragIconSize,
                                                                    'k-pl-2 k-pr-2',
                                                                )}
                                                            />
                                                        ) : (
                                                            <FontAwesomeIcon
                                                                icon={['far', 'hyphen']}
                                                                className={cn(styles.sortAscIcon, 'k-pl-2 k-pr-2')}
                                                            />
                                                        ))}
                                                </div>
                                                {!isFirstReviewLog && parseValue(reviewLog.y_volume)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div className={cn(style.editSiteObservationInfo, 'k-pt-sm k-pl-md k-pr-md')}>
                    <div className={cn(style.pipeGap, 'k-shrink-0 k-d-flex k-flex')}>
                        <div className="k-flex k-justify-content-self-start k-flex-row k-d-flex k-align-items-center !k-basis-8/12 k-gap-12">
                            <div className="k-font-medium">{t('reviewLogDialog.type')}</div>
                            <div className={cn(style.editSiteObservationTopInfoLabel, 'k-pl-1')}>{reviewLog.type}</div>
                        </div>
                        <div className="k-flex k-justify-content-self-start k-flex-row k-d-flex k-align-items-center !k-basis-6/12 k-gap-14">
                            <div className="k-font-medium">{t('reviewLogDialog.site')}</div>
                            <div className={cn(style.editSiteObservationTopInfoLabel)}>{reviewLog.site}</div>
                        </div>
                    </div>
                </div>
                <div className={cn(style.editSiteObservationInfo, 'k-pt-sm k-pl-md k-pr-md')}>
                    <div className={cn(style.pipeGap, 'k-shrink-0 k-d-flex k-flex')}>
                        <div className="k-flex k-justify-content-self-start k-flex-row k-d-flex k-align-items-center !k-basis-8/12 k-gap-8">
                            <div className="k-font-medium">{t('reviewLogDialog.dataset')}</div>
                            <div className={cn(style.editSiteObservationTopInfoLabel)}>{reviewLog.dataset}</div>
                        </div>
                        <div className="k-flex k-justify-content-self-start k-flex-row k-d-flex k-align-items-center !k-basis-6/12 k-gap-2">
                            <div className="k-font-medium">{t('reviewLogDialog.numberOfActions')}</div>
                            <div className={cn(style.editSiteObservationTopInfoLabel)}>{reviewLog.actions_count}</div>
                        </div>
                    </div>
                </div>
                <div className={cn(style.editSiteObservationInfo, 'k-pt-sm k-pl-md k-pr-md')}>
                    <div className="k-d-flex k-justify-content-left k-align-items-center k-pb-sm">
                        <div className="k-pr-2 k-align-items-center k-font-medium">
                            {t('reviewLogDialog.association')}
                        </div>
                        <div className={cn(style.editStudyReviewLogLabel, 'k-d-flex k-align-items-center')}>
                            {reviewLog.association}
                            {reviewLog?.association_active === 'No' && (
                                <div className={cn(styles.inactiveIcon, 'k-pl-sm')}>
                                    <InactiveIcon
                                        toolTipText={t('common:inactiveIcon')}
                                        dataTestId="inactive-icon"
                                        tooltipPosition="bottom"
                                        className="k-mr-2"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className={cn(style.editSiteObservationInfo, style.dividerBottom, 'k-pl-md k-pr-md')}>
                    <div className="k-d-flex k-justify-content-left k-align-items-center k-pb-sm k-gap-9">
                        <div className="k-pr-2 k-align-items-center k-font-medium">{t('reviewLogDialog.panel')}</div>
                        <div className={cn(style.editStudyReviewLogLabel, 'k-d-flex k-align-items-center')}>
                            {reviewLog.panel ?? 'N/A'}
                            {reviewLog?.panel_active === 'No' && (
                                <div className={cn(styles.inactiveIcon, 'k-pl-sm')}>
                                    <InactiveIcon
                                        toolTipText={t('common:inactiveIcon')}
                                        dataTestId="inactive-icon"
                                        tooltipPosition="bottom"
                                        className="k-mr-2"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <form id="edit-study-review-log" className="k-w-full k-pt-sm">
                    <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-md k-pl-md k-pr-md">
                        <BasicDropDownController
                            label={t('reviewLogDialog.decision')}
                            control={control}
                            name="decision"
                            data-testid="edit-review-log-decision"
                            data={decisionLov}
                            textField="name"
                            groupField="groupedBy"
                            dataItemKey="id"
                            filterable
                            loading={loadingData}
                            allowClear
                            activeField="active"
                            readOnly={isSubmitting}
                        />
                    </div>
                    <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-lg k-pl-md k-pr-md">
                        <TextAreaController
                            maxLength={3000}
                            control={control}
                            name="comments"
                            data-testid="edit-review-log-comments"
                            label={t('reviewLogDialog.comments')}
                            style={{
                                height: reviewLog.is_scatter_plot === 'Yes' ? '286px' : '299px',
                                overflow: 'auto',
                            }}
                            readOnly={isSubmitting}
                        />
                    </div>
                </form>
            </div>
        </DraggableDialog>
    );
};

export default EditSiteReviewLogDialog;
